import { Form, Input, Modal, Select } from 'antd';
import cx from 'classnames';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Button from '../../components/Button/Button';
import { useContactStore } from '../../hooks/useContactStore';
import { apiInstance } from '../../utils/api';
import RequestProvider, { useRequestContext } from './Request.Provider';
import { ValidatedFormItem } from './RequestComments';

moment.tz.setDefault('Europe/Kiev');

const RequestEditContent = () => {
  const { id } = useParams();
  const location = useHistory();
  const { push } = useHistory();
  const [requestFresh, setRequestFresh] = useState({});

  const {
    request,
    form,
    clickedSubmit,
    formContent,
    handleClickForm,
    onValuesFormChange,
    successModal,
    errorModal,
    setErrorModal,
    setSuccessModal,
  } = useRequestContext();

  const { currentContactStore } = useContactStore();
  const isContractorStore =
    currentContactStore?.type?.crmId === 'f3c0ce97-53e6-df11-971b-001d60e938c6';

  // Загружаем данные заявки по id
  useEffect(() => {
    apiInstance(
      `${process.env.REACT_APP_API_URL}/api/crm/get-data?id=${id}&type=requestQuery`,
      'GET'
    ).then((resp) => {
      const data = resp.rows?.[0] || {};
      // console.log(resp.rows?.[0], 'data');
      setRequestFresh(data);
    });
  }, [id]);

  // Обновляем initialValues, добавляя SuWork
  const initialValues = useMemo(() => {
    if (!requestFresh) return {};

    const fields = {};

    for (const [key, value] of Object.entries(requestFresh)) {
      if (value && typeof value === 'object' && 'value' in value) {
        fields[key] = value.value;
      } else {
        fields[key] = value;
      }
    }

    // Добавляем отдельные поля времени для компонента RequestSuWorkField
    if (requestFresh.SuStartWork) {
      fields.SuStartWork = requestFresh.SuStartWork;
    }

    if (requestFresh.SuEndWork) {
      fields.SuEndWork = requestFresh.SuEndWork;
    }

    // Добавляем SuWork, если есть SuStartWork и SuEndWork
    if (requestFresh.SuStartWork && requestFresh.SuEndWork) {
      fields.SuWork = [moment(requestFresh.SuStartWork), moment(requestFresh.SuEndWork)];

      // Добавляем отдельные поля для редактирования
      fields.SuStartWork = requestFresh.SuStartWork;
      fields.SuEndWork = requestFresh.SuEndWork;
    }

    return fields;
  }, [requestFresh]);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [form, initialValues]);

  // Обработка успешного/ошибочного статуса
  useEffect(() => {
    if (successModal) {
      setTimeout(() => {
        setSuccessModal(false);
        location.push('/');
      }, 1000);
    }
    if (errorModal) {
      setTimeout(() => {
        setErrorModal(false);
        push(`/requests/${id}`);
      }, 4000);
    }
  }, [successModal, errorModal, location, push, id]);

  const processedFormContent = useMemo(() => {
    const content = formContent[requestFresh?.Category?.value];
    if (!isContractorStore || !content) return content;

    const removeRespContractor = (node) => {
      if (!node) return node;
      if (Array.isArray(node)) {
        return node.map(removeRespContractor).filter(Boolean);
      }
      if (typeof node !== 'object') return node;
      if (node?.props?.name === 'RespContractorName') return null;
      if (node?.props?.children) {
        const newChildren = removeRespContractor(node.props.children);
        return React.cloneElement(node, { ...node.props }, newChildren);
      }
      return node;
    };

    return removeRespContractor(content);
  }, [formContent, requestFresh?.Category?.value, isContractorStore]);

  return (
    <>
      <div className="request-page-create boxes" style={{ flexDirection: 'column' }}>
        <div className="box create-request">
          <div className="text-black-medium-f22-l30">Редагування звернення</div>

          <div className="text-grey-dark-normal-f16-l28">
            Будь ласка, заповніть усі необхідні дані
          </div>
          <div className="create-request__container field-grey-straight-border">
            <div className="request-ediatable">
              <div className="request-ediatable-id">
                <div className="request-ediatable-id-label">ID звернення:</div>
                <div className="request-ediatable-id-value">{request?.Number}</div>
              </div>
              <div className="request-ediatable-content">
                {' '}
                <Form
                  onValuesChange={(e) => onValuesFormChange(e)}
                  form={form}
                  validateTrigger={['onSubmit', 'onChange']}
                  className={cx('request-ediatable-form', { disabled: clickedSubmit })}
                  initialValues={initialValues} // Используем initialValues
                >
                  <Form.Item name="Category" label="Тип звернення">
                    <Select
                      disabled
                      value={requestFresh?.Category?.value}
                      placeholder={requestFresh?.Category?.displayValue || 'Тип звернення'}
                      options={[
                        {
                          label: requestFresh?.Category?.displayValue || 'Тип звернення',
                          value: requestFresh?.Category?.value,
                        },
                      ]}
                    ></Select>
                  </Form.Item>
                  {requestFresh?.ServiceCategory && (
                    <Form.Item name="ServiceCategory" label="Категорія сервісу">
                      <Select
                        disabled
                        value={requestFresh?.ServiceCategory?.value}
                        placeholder={requestFresh?.ServiceCategory?.displayValue || 'Категорія'}
                        options={[
                          {
                            label: requestFresh?.ServiceCategory?.displayValue || 'Тип звернення',
                            value: requestFresh?.ServiceCategory?.value,
                          },
                        ]}
                      ></Select>{' '}
                    </Form.Item>
                  )}{' '}
                  {requestFresh?.ServiceCategory && (
                    <ValidatedFormItem
                      name="SuWorkDescription"
                      label={
                        requestFresh?.ServiceCategory?.value ===
                          '88a699c2-b2de-4bde-9ffd-c038a9a174f9'
                          ? 'Мета візиту'
                          : 'Опис робіт'
                      }
                      placeholder={
                        requestFresh?.ServiceCategory?.value ===
                          '88a699c2-b2de-4bde-9ffd-c038a9a174f9'
                          ? 'Вкажіть мету візиту'
                          : 'Вкажіть опис робіт'
                      }
                      errorMessage={
                        requestFresh?.ServiceCategory?.value ===
                          '88a699c2-b2de-4bde-9ffd-c038a9a174f9'
                          ? 'Вкажіть мету візиту'
                          : 'Вкажіть опис робіт'
                      }
                      required={true}
                      disabled={clickedSubmit}
                      inputType="input"
                      validateTrigger={['onSubmit', 'onChange']}
                    />
                  )}
                  {processedFormContent}
                  <Form.Item>
                    <Button
                      style={{ width: '100%' }}
                      onClick={handleClickForm}
                      type="submit"
                      text="Надіслати"
                      bg="pink"
                      disabled={clickedSubmit}
                    />
                  </Form.Item>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        className="success-modal"
        visible={errorModal}
        footer={null}
        onCancel={() => {
          setErrorModal(false);
        }}
      >
        <div className="success-modal__wrapper">
          <div className="text-black-medium-f22-l30">Помилка</div>
          <div className="text-grey-dark-normal-f16-l28">
            Вибачте, статус звернення було змінено, ви не можете редагувати звернення з цим
            статусом. Вас буде перенаправлено на сторінку звернення.
          </div>
        </div>
      </Modal>
      <Modal
        className="success-modal"
        visible={successModal}
        footer={null}
        onCancel={() => setSuccessModal(false)}
      >
        <div className="success-modal__wrapper">
          <div className="text-black-medium-f22-l30">Дякуємо</div>
          <div className="text-grey-dark-normal-f16-l28">
            Ми прийняли ваше звернення та зв’яжемось із вами найближчим часом.
          </div>
        </div>
      </Modal>
    </>
  );
};

export default RequestEditContent;
